import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FiHeart, FiTrash2, FiShoppingCart } from 'react-icons/fi';
import { toast } from 'react-toastify';
import wishlistService from '../../services/wishlist.service';
import { useLanguage } from '../../context/LanguageContext';
import { useCart } from '../../context/CartContext';
import LoadingSpinner from '../../components/common/LoadingSpinner';

const WishlistPage = () => {
  const { t } = useLanguage();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const { addToCart } = useCart();

  useEffect(() => {
    const fetchWishlist = async () => {
      try {
        const { data } = await wishlistService.getAll();
        setItems(data.data);
      } catch (error) {
        toast.error('Failed to load wishlist');
      } finally {
        setLoading(false);
      }
    };
    fetchWishlist();
  }, []);

  const handleRemove = async (productId) => {
    try {
      await wishlistService.remove(productId);
      setItems((prev) => prev.filter((item) => item.productId !== productId));
      toast.success('Removed from wishlist');
    } catch (error) {
      toast.error('Failed to remove');
    }
  };

  const handleAddToCart = async (item) => {
    try {
      await addToCart(item.productId);
      toast.success(`${item.name} added to cart!`);
    } catch (error) {
      toast.error('Failed to add to cart');
    }
  };

  if (loading) return <LoadingSpinner />;

  return (
    <div className="section-padding">
      <div className="container" style={{ maxWidth: '900px' }}>
        <h2 className="mb-4">
          <FiHeart className="me-2" style={{ color: '#B91C1C' }} />
          My Wishlist
          <span className="text-muted fs-6 ms-2">({items.length} items)</span>
        </h2>

        {items.length === 0 ? (
          <div className="dashboard-card text-center py-5">
            <FiHeart size={48} className="text-muted mb-3" />
            <h5 className="text-muted">{t('wishlistEmpty')}</h5>
            <p className="text-muted">{t('wishlistEmptyDesc')}</p>
            <Link to="/products" className="btn btn-ebw-primary mt-2">{t('browseProducts')}</Link>
          </div>
        ) : (
          <div className="row g-3">
            {items.map((item) => (
              <div key={item._id} className="col-sm-6 col-md-4">
                <div className="dashboard-card h-100">
                  <Link to={`/products/${item.slug}`} className="text-decoration-none">
                    {item.image ? (
                      <img
                        src={item.image} alt={item.name}
                        className="w-100 rounded mb-3"
                        style={{ height: '180px', objectFit: 'cover' }}
                      />
                    ) : (
                      <div className="w-100 rounded mb-3 bg-light d-flex align-items-center justify-content-center"
                        style={{ height: '180px' }}>
                        <span className="text-muted">No Image</span>
                      </div>
                    )}
                    <h6 className="text-dark mb-1 text-truncate">{item.name}</h6>
                  </Link>
                  <p className="text-primary fw-bold mb-3">${item.price?.toFixed(2)}</p>
                  <div className="d-flex gap-2">
                    <button
                      className="btn btn-ebw-primary btn-sm flex-grow-1"
                      onClick={() => handleAddToCart(item)}
                    >
                      <FiShoppingCart size={14} className="me-1" /> Add to Cart
                    </button>
                    <button
                      className="btn btn-outline-danger btn-sm"
                      onClick={() => handleRemove(item.productId)}
                      title="Remove from wishlist"
                    >
                      <FiTrash2 size={14} />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default WishlistPage;
